import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { ChangeDetectorRef, Component, Inject, ViewEncapsulation } from '@angular/core';
import { MatChipEditedEvent, MatChipInputEvent, MatChipListboxChange } from '@angular/material/chips';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSelectionListChange } from '@angular/material/list';
import { ViewStateRecord } from '@common/data-models/records/viewstate-record';
import { FilterTypes } from '@common/enums/filter-types';
import { MultipleFeedViewStateMode, ViewStateTypes, ViewStateTypesEnum } from '@common/enums/viewstate-types';
import { GlobalTablesService } from '@common/services/global-tables-service';
import { LanguageSettings } from '@common/services/language-settings';
import { LayoutState } from '@common/services/layout-state';
import { NavigationService } from '@common/services/navigation-service';
import { NomWebServicesJson } from '@common/services/nom-web-services-json';
import { selectTranslatedKey } from '@common/store/i18n/selectors/i18n.selectors';
import { NavigationGroup, NavigationItem, NavigationSection } from '@common/store/navigation/models/navigation.models';
import { CreateViewStateActions } from '@src/suite/common/store/createviewstate/createviewstate.actions';
import {
    selectCreateIsInAddContentSourceMode,
    selectCreateIsInBrowseMode,
    selectCreateIsInConfigureMode,
    selectCreateMultipleFeedViewStateMode,
    selectCreateViewStateFeedID,
    selectCreateViewStateFeedURL,
    selectCreateViewStateGroupBy,
    selectCreateViewStateLayout,
    selectCreateViewStateSequence,
    selectCreateViewStateSequenceAll,
    selectCreateViewStateSequenceSelected,
    selectCreateViewStateSortBy,
    selectCreateViewStateTwitterUsernames,
    selectDiscoverCreateViewStateTypeOptions,
    selectFilterViewStateSequence,
} from '@src/suite/common/store/createviewstate/createviewstate.selectors';
import { i18nKeys } from '@src/suite/common/utils/i18n.default';
import {
    selectAPFeeds,
    selectFeedProviderIsLoading,
    selectFTPFeeds,
    selectHasAPFeeds,
    selectHasFTPFeeds,
    selectHasTwitterCredentials,
    selectTwitterCredentials,
} from '@src/suite/discover/store/selectors/feed.discover.selectors';
import { combineLatest, debounceTime, map, Observable } from 'rxjs';

import { BaseViewState } from './base-view-state';

@Component({
    selector: 'st-discover-view-state',
    templateUrl: './discover-view-state.html',
    styleUrls: ['./discover-view-state.scss'],
    encapsulation: ViewEncapsulation.None,
    // changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'st-discover-view-state',
    },
})
export class DiscoverViewStateComponent extends BaseViewState {
    public viewStateTypes = ViewStateTypesEnum;
    public mutipleFeedViewStateModes = MultipleFeedViewStateMode;
    public filterTypes = FilterTypes;

    readonly separatorKeysCodes = [ENTER, COMMA] as const;

    twitterUsernames: Array<string> = [];

    inAddContentSourceMode$ = this.store.select(selectCreateIsInAddContentSourceMode);
    inConfigureMode$ = this.store.select(selectCreateIsInConfigureMode);
    inBrowseMode$ = this.store.select(selectCreateIsInBrowseMode);
    mutipleFeedViewStateMode$ = this.store.select(selectCreateMultipleFeedViewStateMode);

    apFeeds$ = this.store.select(selectAPFeeds);
    ftpFeeds$ = this.store.select(selectFTPFeeds);
    isLoading$ = this.store.select(selectFeedProviderIsLoading);
    hasAPFeeds$ = this.store.select(selectHasAPFeeds);
    hasFTPFeeds$ = this.store.select(selectHasFTPFeeds);

    hasTwitterCredentials$ = this.store.select(selectHasTwitterCredentials);
    twitterCredentials$ = this.store.select(selectTwitterCredentials);
    apFeedLabel$ = this.store.select(selectTranslatedKey(i18nKeys.PSTWebDiscoverApFeedLabel));
    ftpFeedLabel$ = this.store.select(selectTranslatedKey(i18nKeys.PSTWebDiscoverFtpFeedLabel));

    viewStateLayout$ = this.store.select(selectCreateViewStateLayout);
    viewStateGroupBy$ = this.store.select(selectCreateViewStateGroupBy);
    viewStateSortBy$ = this.store.select(selectCreateViewStateSortBy);

    viewStateFeedURL$ = this.store.select(selectCreateViewStateFeedURL);
    viewStateFeedID$ = this.store.select(selectCreateViewStateFeedID);
    viewStateTwitterUsernames$ = this.store.select(selectCreateViewStateTwitterUsernames);

    viewStateSequence$ = this.store.select(selectCreateViewStateSequence);
    viewStateSequenceSelected$ = this.store.select(selectCreateViewStateSequenceSelected);
    viewStateSequenceAll$ = this.store.select(selectCreateViewStateSequenceAll);

    viewStateTypeOptions$ = this.store.select(selectDiscoverCreateViewStateTypeOptions);

    get isRssFeedsDisabled$(): Observable<boolean> {
        return combineLatest([this.viewStateType$, this.inEditMode$, this.inAddContentSourceMode$, this.mutipleFeedViewStateMode$]).pipe(
            map(([vstype, edit, add, mode]) => {
                if (add && mode === MultipleFeedViewStateMode.CONFIGURE) return false;
                return vstype !== ViewStateTypes.RSSFEED && edit;
            })
        );
    }

    get isAPFeedsDisabled$(): Observable<boolean> {
        return combineLatest([this.hasAPFeeds$, this.viewStateType$, this.inEditMode$, this.inAddContentSourceMode$, this.mutipleFeedViewStateMode$]).pipe(
            map(([value, vstype, edit, add, mode]) => {
                if (add && mode === MultipleFeedViewStateMode.CONFIGURE && value) return false;
                return ((vstype !== ViewStateTypes.APFEED || vstype !== ViewStateTypes.FTPFEED) && edit) || !value;
            })
        );
    }

    get isFTPFeedsDisabled$(): Observable<boolean> {
        return combineLatest([this.hasFTPFeeds$, this.viewStateType$, this.inEditMode$, this.inAddContentSourceMode$, this.mutipleFeedViewStateMode$]).pipe(
            map(([value, vstype, edit, add, mode]) => {
                if (add && mode === MultipleFeedViewStateMode.CONFIGURE && value) return false;
                return ((vstype !== ViewStateTypes.APFEED || vstype !== ViewStateTypes.FTPFEED) && edit) || !value;
            })
        );
    }

    get isTwitterFeedsDisabled$(): Observable<boolean> {
        return combineLatest([this.hasTwitterCredentials$, this.viewStateType$, this.inEditMode$, this.inAddContentSourceMode$, this.mutipleFeedViewStateMode$]).pipe(
            map(([value, vstype, edit, add, mode]) => {
                if (add && mode === MultipleFeedViewStateMode.CONFIGURE && value) return false;
                return (vstype !== ViewStateTypes.TWITTERFEED && edit) || !value;
            })
        );
    }

    updateMultipleFeedViewStateMode(mode: MultipleFeedViewStateMode) {
        if (mode === MultipleFeedViewStateMode.BROWSE) this.store.dispatch(CreateViewStateActions.enterBrowseMode());
        if (mode === MultipleFeedViewStateMode.CONFIGURE) this.store.dispatch(CreateViewStateActions.enterConfigureMode());
    }

    constructor(
        private nomWebServicesJson: NomWebServicesJson,
        public viewStateService: LayoutState,
        public navigationService: NavigationService,
        public dialogRef: MatDialogRef<DiscoverViewStateComponent>,
        public changeDetectorRef: ChangeDetectorRef,
        public globalTablesService: GlobalTablesService,
        public languageSettings: LanguageSettings,
        @Inject(MAT_DIALOG_DATA)
        public data: {
            copy: boolean;
            edit: boolean;
            vs: ViewStateRecord;
            group: NavigationGroup;
            section: NavigationSection;
            item: NavigationItem;
            mt: string;
        }
    ) {
        super();
        this.store.dispatch(
            CreateViewStateActions.initializeViewState({
                vstype: data.vs?.viewStateType || ViewStateTypes.RSSFEED,
                copy: data.copy,
                edit: data.edit,
                vs: data.vs,
                group: data.group,
                section: data.section,
                item: data.item,
            })
        );
    }

    private search: string = '';
    private types: Array<number> = [];

    filterViewStates(value: string) {
        this.search = value ? value.toLowerCase() : '';
        this.viewStateSequenceAll$ = this.store.select(selectFilterViewStateSequence(this.search, this.types)).pipe(debounceTime(300));
    }

    onFeedTypeChange(event: MatChipListboxChange) {
        this.types = event.value.map((str: string) => parseInt(str));
        this.viewStateSequenceAll$ = this.store.select(selectFilterViewStateSequence(this.search, this.types)).pipe(debounceTime(300));
    }

    onSelectionChange(event: MatSelectionListChange) {
        const selected = event.source.selectedOptions.selected.map(x => x.value);
        const unselected = event.source.options.filter(x => !selected.find(y => y === x.value)).map(x => x.value);
        this.store.dispatch(CreateViewStateActions.updateViewStateSequence({ selected: selected, unselected: unselected }));
    }

    onBack = () => {
        this.store.dispatch(CreateViewStateActions.cancelAddViewStates());
    };

    onAddViewState = async () => {
        this.store.dispatch(CreateViewStateActions.saveAddViewStates());
    };

    public async onAddCountentSource() {
        this.viewStateSequenceAll$ = this.store.select(selectCreateViewStateSequenceAll);
        this.store.dispatch(CreateViewStateActions.enterBrowseMode());
    }

    updateViewStateFeedURL(url: string) {
        this.store.dispatch(CreateViewStateActions.updateViewStateFeedUrl({ url }));
    }

    updateViewStateFeedID(id: string) {
        this.store.dispatch(CreateViewStateActions.updateViewStateFeedId({ id }));
    }

    updateViewStateTwitterUsernames() {
        this.store.dispatch(CreateViewStateActions.updateViewStateTwitterUsernames({ usernames: this.twitterUsernames }));
    }

    addTwitterUsername(event: MatChipInputEvent): void {
        const value = (event.value || '').trim();

        if (value.length > 1 && value.startsWith('@')) {
            this.twitterUsernames.push(value);
            event.chipInput!.clear();
        }

        this.updateViewStateTwitterUsernames();
    }

    removeTwitterUsername(twitterUsername: string): void {
        const index = this.twitterUsernames.indexOf(twitterUsername);

        if (index >= 0) {
            this.twitterUsernames.splice(index, 1);
        }

        this.updateViewStateTwitterUsernames();
    }

    editTwitterUsername(twitterUsername: string, event: MatChipEditedEvent) {
        const value = event.value.trim();

        if (!value) {
            this.removeTwitterUsername(twitterUsername);
            return;
        }

        const index = this.twitterUsernames.indexOf(twitterUsername);
        if (index >= 0) {
            this.twitterUsernames[index] = value;
        }

        this.updateViewStateTwitterUsernames();
    }

    onRemoveAllContentSource() {
        this.store.dispatch(CreateViewStateActions.removeAllViewStates());
    }

    onRemoveContentSource(guid: string) {
        this.store.dispatch(CreateViewStateActions.removeViewState({ guid }));
    }
}
